import { TickCircle } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Divider } from '@/components/Divider/Divider.component'
import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/ResponsiveModal/ResponsiveModal'
import { Button } from '@/components/ui/button'
import { useAppDispatch } from '@/hooks/redux'
import { updateUserPdfLanguage } from '@/store/api/Users/Users.slice'
import { SocialMirrorPalette } from '@/utils/palette/SocialMirrorPalette'

export const ReportLanguagePopUp = ({
  open,
  setOpen,
  currentLang,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  currentLang: string
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    currentLang || 'en',
  )

  const handleLanguageSelect = (language: string) => {
    setSelectedLanguage(language)
  }

  const handleConfirm = () => {
    dispatch(updateUserPdfLanguage({ pdfLanguage: selectedLanguage }))
    setOpen(false)
  }

  return (
    <ResponsiveModal open={open} onOpenChange={setOpen}>
      <ResponsiveModalTrigger>
        <Button
          variant="outline"
          className="py-2 px-4 border-neutralColor30 rounded-3xl"
        >
          {t('common.edit')}
        </Button>
      </ResponsiveModalTrigger>
      <ResponsiveModalContent
        className="bg-baseColor flex flex-col gap-6 md:max-w-2xl"
        side="bottom"
      >
        <ResponsiveModalHeader>
          <ResponsiveModalTitle className="text-[32px] max-sm:text-2xl font-medium text-neutralColor">
            {t('dashboard.settings.reportSettings.reportLanguage')}
          </ResponsiveModalTitle>
        </ResponsiveModalHeader>

        <div
          className={`py-4 px-5 rounded-2xl cursor-pointer transition-all duration-200 bg-baseColorHighlight ${
            selectedLanguage === 'en' && 'border border-neutralColor30 '
          }`}
          onClick={() => handleLanguageSelect('en')}
        >
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-medium text-neutralColor">
              {t('dashboard.settings.english')}
            </h3>
            {selectedLanguage === 'en' && (
              <TickCircle
                size="24"
                color={SocialMirrorPalette.neutralColor}
                variant="Bold"
              />
            )}
          </div>
        </div>

        <div
          className={`py-4 px-5 rounded-2xl cursor-pointer transition-all duration-200 bg-baseColorHighlight ${
            selectedLanguage === 'es' && 'border border-neutralColor30 '
          }`}
          onClick={() => handleLanguageSelect('es')}
        >
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-medium text-neutralColor">
              {t('dashboard.settings.spanish')}
            </h3>
            {selectedLanguage === 'es' && (
              <TickCircle
                size="24"
                color={SocialMirrorPalette.neutralColor}
                variant="Bold"
              />
            )}
          </div>
        </div>

        <Divider />

        <div className="flex gap-3 justify-end">
          <ResponsiveModalClose asChild>
            <Button
              variant="secondary"
              type="button"
              className="font-medium text-base h-[46px] px-5 rounded-3xl py-3"
            >
              <p className="text-base font-medium">{t('common.cancel')}</p>
            </Button>
          </ResponsiveModalClose>
          <Button
            onClick={handleConfirm}
            type="button"
            className="font-medium text-base h-[46px] px-5 rounded-3xl py-3"
          >
            <p className="text-base font-medium">{t('common.confirm')}</p>
          </Button>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
